import { useLocation } from "react-router-dom";
import { Menu, ShieldCheck } from "lucide-react";

import { getRouteMeta } from "@/config/navigation";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { GlobalSearch } from "@/components/shell/GlobalSearch";
import { QuickCreate } from "@/components/shell/QuickCreate";
import { NotificationsPanel } from "@/components/shell/NotificationsPanel";
import { ResaPanel } from "@/components/shell/ResaPanel";
import { ProfileMenu } from "@/components/shell/ProfileMenu";
import { SHELL } from "@/constants/testIds";

/**
 * Persistent top bar: page title (left) + global actions (right):
 * search, + Create, notifications, RESA entry, profile menu.
 */
export const Topbar = ({ onMenuClick }) => {
  const { pathname } = useLocation();
  const { title, section } = getRouteMeta(pathname);
  const { platformRole } = useAuth();

  return (
    <header
      data-testid={SHELL.topbar}
      className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-background/85 px-4 backdrop-blur-md sm:px-6"
    >
      <button
        onClick={onMenuClick}
        data-testid={SHELL.sidebarToggle}
        className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground lg:hidden"
        aria-label="Open navigation"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div className="min-w-0">
        {section && (
          <p className="hidden text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground sm:block">
            {section}
          </p>
        )}
        <p data-testid={SHELL.pageTitle} className="truncate text-base font-bold text-foreground sm:text-lg">
          {title}
        </p>
      </div>

      <div className="ml-auto flex items-center gap-2 sm:gap-3">
        {platformRole && (
          <Badge
            data-testid="platform-staff-indicator"
            className="hidden bg-brand-navy text-white hover:bg-brand-navy sm:inline-flex"
          >
            <ShieldCheck className="mr-1 h-3 w-3 text-brand-gold" />
            {platformRole === "super_admin" ? "Super Admin" : "Support Admin"}
          </Badge>
        )}
        <div className="hidden md:block">
          <GlobalSearch />
        </div>
        <div className="hidden sm:block">
          <QuickCreate />
        </div>
        <NotificationsPanel />
        <ResaPanel />
        <ProfileMenu />
      </div>
    </header>
  );
};
